import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

const GENDERS = [
  { value: "", label: "Tous" },
  { value: "homme", label: "Homme" },
  { value: "femme", label: "Femme" },
];

const CATEGORIES = [
  { value: "", label: "Tout" },
  { value: "hoodies", label: "Hoodies" },
  { value: "bonnets", label: "Bonnets" },
  { value: "tshirts", label: "T-shirts" },
  { value: "manches-longues", label: "Manches longues" },
  { value: "vestes", label: "Manteaux & Vestes" },
];

const SIZES = ["", "S", "M", "L", "XL", "XXL", "Unique"];

const COLORS = [
  { value: "", label: "Toutes", hex: null },
  { value: "Noir", label: "Noir", hex: "#0A0A0A" },
  { value: "Blanc", label: "Blanc", hex: "#F5F5F5" },
  { value: "Gris beton", label: "Gris béton", hex: "#8A8A8E" },
  { value: "Cyan electrique", label: "Cyan", hex: "#00F0FF" },
];

export default function Shop() {
  const [gender, setGender] = useState("");
  const [category, setCategory] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (gender) params.gender = gender;
    if (category) params.category = category;
    if (size) params.size = size;
    if (color) params.color = color;
    api
      .get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [gender, category, size, color]);

  const chip = (active) =>
    `font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
      active
        ? "border-ice text-ice bg-ice/10"
        : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
    }`;

  return (
    <div className="px-4 sm:px-6 lg:px-12 pt-32 pb-24" data-testid="shop-page">
      <Seo
        title="Boutique — S'NO"
        siteName="S'NO"
        description="Hoodies, bonnets, t-shirts, manches longues et vestes de snow 20K pour homme et femme. Streetwear technique S'NO."
        image="/logo.webp"
      />

      <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">Catalogue FW26</p>
      <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-none mb-12">
        La boutique
      </h1>

      {/* Gender */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="filter-gender">
        {GENDERS.map((g) => (
          <button
            key={g.value}
            data-testid={`filter-gender-${g.value || "tous"}`}
            onClick={() => setGender(g.value)}
            className={`font-display uppercase tracking-[0.2em] text-lg px-6 py-2.5 border transition-colors duration-300 ${
              gender === g.value
                ? "border-ice bg-ice text-black"
                : "border-white/15 text-zinc-300 hover:border-white/40"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="filter-category">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            data-testid={`filter-category-${c.value || "tout"}`}
            onClick={() => setCategory(c.value)}
            className={chip(category === c.value)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Sizes + colors */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mb-12">
        <div className="flex flex-wrap gap-2" data-testid="filter-size">
          {SIZES.map((s) => (
            <button
              key={s || "all"}
              data-testid={`filter-size-${s || "toutes"}`}
              onClick={() => setSize(s)}
              className={chip(size === s)}
            >
              {s || "Tailles"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2" data-testid="filter-color">
          {COLORS.map((c) => (
            <button
              key={c.value || "all"}
              data-testid={`filter-color-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => setColor(c.value)}
              title={c.label}
              aria-label={`Couleur ${c.label}`}
              className={`w-7 h-7 rounded-full border-2 transition-all duration-300 ${
                color === c.value ? "border-ice scale-110" : "border-white/20 hover:border-white/60"
              }`}
              style={{
                background: c.hex || "conic-gradient(#0A0A0A, #F5F5F5, #8A8A8E, #00F0FF, #0A0A0A)",
              }}
            />
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="shop-results-count">
          {products.length} pièce{products.length > 1 ? "s" : ""}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-smoke border border-white/10 animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-24 text-center" data-testid="shop-empty-state">
          <p className="font-display uppercase text-2xl text-zinc-500">
            Aucune pièce ne match ces filtres
          </p>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="shop-grid">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
